// communityActions/styled.js
import { styled } from '@mui/material/styles';
import { Box, Chip, Fab, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert } from '@mui/material';


// Honey colors (consistent with your theme)
const honeyColors = {
  primary: '#F1C40F',
  primaryLight: '#F4D03F',
  primaryDark: '#D4AC0D',
  amber: '#FFB74D',
  cream: '#FFF8E1',
  honeycomb: '#D4A574',
  surface: '#FFFFFF',
  surfaceHoney: '#FFF9E6',
  border: '#F5E6B3',
  textPrimary: '#3E2723',
  textSecondary: '#5D4037',
  textMuted: '#8D6E63',
  warning: '#FF9800',
  info: '#29B6F6',
  success: '#66BB6A',
  error: '#EF5350',
  gradientPrimary: 'linear-gradient(135deg, #F1C40F 0%, #FFB74D 100%)',
  gradientHoneycomb: 'linear-gradient(135deg, #D4A574 0%, #F1C40F 50%, #FFB74D 100%)',
  gradientAccent: 'linear-gradient(135deg, #FFB74D 0%, #FF8A65 100%)',
  shadowLight: '0 2px 8px rgba(212, 165, 116, 0.12)',
  shadowMedium: '0 4px 12px rgba(212, 165, 116, 0.18)',
  shadowHeavy: '0 8px 24px rgba(212, 165, 116, 0.25)',
  shadowFab: '0 8px 32px rgba(241, 196, 15, 0.3)',
};

// Modified Floating Action Button - REMOVED FIXED POSITIONING
export const JoinFab = styled(Fab)({
  // REMOVED: position: 'fixed', bottom: '32px', right: '32px',
  zIndex: 1000,
  background: honeyColors.gradientPrimary,
  color: honeyColors.textPrimary,
  borderRadius: '28px',
  minWidth: '160px',
  height: '56px',
  boxShadow: honeyColors.shadowFab,
  border: `2px solid rgba(255, 255, 255, 0.2)`,
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    background: honeyColors.gradientHoneycomb,
    transform: 'translateY(-4px) scale(1.02)',
    boxShadow: '0 12px 40px rgba(241, 196, 15, 0.4)',
  },

  '&:active': {
    transform: 'translateY(-2px) scale(0.98)',
  },

  '& .MuiFab-label': {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontWeight: 700,
    fontSize: '0.95rem',
    textTransform: 'none',
    letterSpacing: '0.5px',
  },

  '& .MuiSvgIcon-root': {
    fontSize: '1.3rem',
    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
  },

  // Subtle pulse animation
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '-4px',
    borderRadius: '32px',
    background: honeyColors.gradientPrimary,
    opacity: 0.3,
    animation: 'pulse 2s infinite',
    zIndex: -1,
  },

  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
      opacity: 0.3,
    },
    '50%': {
      transform: 'scale(1.1)',
      opacity: 0.1,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 0.3,
    },
  },
});

// Modern Share Button
export const ShareButton = styled(Button)({
  borderRadius: '16px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '10px 24px',
  border: `2px solid ${honeyColors.border}`,
  color: honeyColors.textSecondary,
  backgroundColor: honeyColors.surface,
  boxShadow: honeyColors.shadowLight,
  transition: 'all 0.2s ease',

  '&:hover': {
    borderColor: honeyColors.primary,
    backgroundColor: honeyColors.surfaceHoney,
    color: honeyColors.textPrimary,
    transform: 'translateY(-2px)',
    boxShadow: honeyColors.shadowMedium,
  },

  '& .MuiSvgIcon-root': {
    marginRight: '8px',
    fontSize: '1.1rem',
  },
});

// Modern Dialog Container
export const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    boxShadow: honeyColors.shadowHeavy,
    border: `1px solid ${honeyColors.border}`,
    background: `linear-gradient(145deg, ${honeyColors.surface} 0%, ${honeyColors.surfaceHoney} 100%)`,
    backdropFilter: 'blur(20px)',
    overflow: 'hidden', // Changed from 'visible' to 'hidden'
    position: 'relative',

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: honeyColors.gradientPrimary,
      borderRadius: '22px 22px 0 0', // Slightly smaller radius to fit inside
    },
  },

  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(62, 39, 35, 0.4)',
    backdropFilter: 'blur(8px)',
  },
});

// Enhanced Dialog Title
export const StyledDialogTitle = styled(DialogTitle)({
  backgroundColor: 'transparent',
  padding: '24px 32px 16px',
  borderBottom: 'none',

  '& .MuiTypography-root': {
    fontWeight: 700,
    fontSize: '1.5rem',
    color: honeyColors.textPrimary,
    letterSpacing: '-0.5px',
  },

  '& .MuiSvgIcon-root': {
    color: honeyColors.primary,
    fontSize: '1.8rem',
    filter: 'drop-shadow(0 2px 4px rgba(241, 196, 15, 0.3))',
  },

  '& .MuiIconButton-root': {
    backgroundColor: honeyColors.surfaceHoney,
    border: `1px solid ${honeyColors.border}`,
    borderRadius: '12px',
    width: '40px',
    height: '40px',
    transition: 'all 0.2s ease',

    '&:hover': {
      backgroundColor: honeyColors.cream,
      borderColor: honeyColors.primary,
      transform: 'scale(1.05)',
    },
  },
});

// Enhanced Dialog Content
export const StyledDialogContent = styled(DialogContent)({
  backgroundColor: 'transparent',
  padding: '0 32px 24px',

  '& .MuiTextField-root': {
    marginBottom: '16px',

    '& .MuiOutlinedInput-root': {
      borderRadius: '16px',
      backgroundColor: honeyColors.surface,
      border: `1px solid ${honeyColors.border}`,
      transition: 'all 0.2s ease',

      '&:hover': {
        borderColor: honeyColors.primary,
        backgroundColor: honeyColors.surfaceHoney,
      },

      '&.Mui-focused': {
        borderColor: honeyColors.primary,
        backgroundColor: honeyColors.surface,
        boxShadow: `0 0 0 3px rgba(241, 196, 15, 0.1)`,
      },

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },

    '& .MuiInputLabel-root': {
      color: honeyColors.textMuted,
      fontWeight: 500,

      '&.Mui-focused': {
        color: honeyColors.primary,
      },
    },

    '& .MuiInputBase-input': {
      color: honeyColors.textPrimary,
      fontWeight: 500,

      '&::placeholder': {
        color: honeyColors.textMuted,
        opacity: 1,
      },
    },
  },
});

// Modern Alert Component
export const StyledAlert = styled(Alert)({
  borderRadius: '16px',
  backgroundColor: 'rgba(41, 182, 246, 0.1)',
  border: `1px solid rgba(41, 182, 246, 0.3)`,
  color: honeyColors.info,
  marginBottom: '24px',

  '& .MuiAlert-icon': {
    color: honeyColors.info,
  },

  '& .MuiAlert-message': {
    fontWeight: 500,
  },
});

// Enhanced Dialog Actions
export const StyledDialogActions = styled(DialogActions)({
  padding: '20px 32px 32px',
  backgroundColor: 'transparent',
  gap: '12px',

  '& .MuiButton-root': {
    borderRadius: '16px',
    textTransform: 'none',
    fontWeight: 600,
    padding: '12px 32px',
    fontSize: '0.95rem',
    minWidth: '120px',
    transition: 'all 0.2s ease',

    '&.MuiButton-text': {
      color: honeyColors.textMuted,
      backgroundColor: 'transparent',
      border: `1px solid ${honeyColors.border}`,

      '&:hover': {
        backgroundColor: honeyColors.surfaceHoney,
        borderColor: honeyColors.primary,
        color: honeyColors.textPrimary,
      },
    },

    '&.MuiButton-contained': {
      background: honeyColors.gradientPrimary,
      color: honeyColors.textPrimary,
      boxShadow: honeyColors.shadowMedium,
      border: 'none',

      '&:hover': {
        background: honeyColors.gradientHoneycomb,
        transform: 'translateY(-2px)',
        boxShadow: honeyColors.shadowHeavy,
      },

      '&:disabled': {
        background: honeyColors.textMuted,
        color: honeyColors.surface,
        opacity: 0.6,
        transform: 'none',
      },
    },
  },
});

// Modern QR Code Container
export const QRCodeContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '32px 24px',
  backgroundColor: honeyColors.surface,
  borderRadius: '20px',
  border: `2px solid ${honeyColors.border}`,
  boxShadow: honeyColors.shadowLight,
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: honeyColors.gradientPrimary,
  },
});

export const QRCodePlaceholder = styled(Box)({
  width: '200px',
  height: '200px',
  border: `3px solid ${honeyColors.primary}`,
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: honeyColors.cream,
  margin: '16px auto',
  position: 'relative',
  background: `
    radial-gradient(circle at 25% 25%, ${honeyColors.surfaceHoney} 2px, transparent 2px),
    radial-gradient(circle at 75% 75%, ${honeyColors.border} 1px, transparent 1px),
    ${honeyColors.cream}
  `,
  backgroundSize: '20px 20px',

  '& .MuiSvgIcon-root': {
    fontSize: '3rem',
    color: honeyColors.primary,
    filter: 'drop-shadow(0 4px 8px rgba(241, 196, 15, 0.3))',
  },
});

// Modern Share Options
export const ShareOptionsGrid = styled(Box)({
  display: 'grid',
  gap: '16px',
  marginTop: '16px',
});

export const ShareOption = styled(Paper)({
  padding: '20px',
  borderRadius: '16px',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  backgroundColor: honeyColors.surface,
  border: `2px solid ${honeyColors.border}`,
  boxShadow: honeyColors.shadowLight,
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    background: honeyColors.gradientPrimary,
    transform: 'translateX(-100%)',
    transition: 'transform 0.3s ease',
  },

  '&:hover': {
    backgroundColor: honeyColors.surfaceHoney,
    borderColor: honeyColors.primary,
    transform: 'translateY(-4px)',
    boxShadow: honeyColors.shadowMedium,

    '&::before': {
      transform: 'translateX(0)',
    },
  },

  '& .MuiSvgIcon-root': {
    fontSize: '2rem',
    color: honeyColors.primary,
    marginBottom: '12px',
    filter: 'drop-shadow(0 2px 4px rgba(241, 196, 15, 0.2))',
  },

  '& .MuiTypography-subtitle1': {
    fontWeight: 700,
    color: honeyColors.textPrimary,
    marginBottom: '4px',
  },

  '& .MuiTypography-body2': {
    color: honeyColors.textMuted,
    fontWeight: 500,
  },
});

// Simplified Link Copy Section - Integrated into main layout
export const LinkCopySection = styled(Box)({
  marginTop: '24px',
  padding: '24px',
  backgroundColor: honeyColors.surfaceHoney,
  borderRadius: '20px',
  border: `2px solid ${honeyColors.border}`,
  position: 'relative',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: honeyColors.gradientPrimary,
    borderRadius: '20px 20px 0 0',
  },

  '& .MuiTextField-root': {
    '& .MuiOutlinedInput-root': {
      paddingRight: 0,
      backgroundColor: honeyColors.surface,
      borderRadius: '16px',

      '& .MuiInputAdornment-root .MuiIconButton-root': {
        backgroundColor: honeyColors.primary,
        color: honeyColors.textPrimary,
        margin: '4px',
        borderRadius: '12px',
        transition: 'all 0.2s ease',

        '&:hover': {
          backgroundColor: honeyColors.primaryDark,
          transform: 'scale(1.05)',
        },
      },
    },
  },
});

// Success Snackbar (you can also style the existing Snackbar)
export const SuccessSnackbar = styled(Box)({
  '& .MuiSnackbar-root': {
    '& .MuiSnackbarContent-root': {
      backgroundColor: honeyColors.success,
      borderRadius: '16px',
      fontWeight: 600,
    },
  },
});

// Section Headers
export const SectionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '20px',

  '& .MuiTypography-root': {
    fontWeight: 700,
    fontSize: '1.25rem',
    color: honeyColors.textPrimary,
    letterSpacing: '-0.5px',
  },

  '& .MuiSvgIcon-root': {
    fontSize: '1.5rem',
    color: honeyColors.primary,
    filter: 'drop-shadow(0 2px 4px rgba(241, 196, 15, 0.3))',
  },
});

// Modern Grid Layout
export const ModernGrid = styled(Box)({
  display: 'grid',
  gap: '24px',

  '@media (min-width: 900px)': {
    gridTemplateColumns: '1fr 1fr',
  },
});

// Download Button
export const DownloadButton = styled(Button)({
  marginTop: '16px',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 20px',
  border: `1px solid ${honeyColors.border}`,
  color: honeyColors.textSecondary,
  backgroundColor: 'transparent',
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: honeyColors.surfaceHoney,
    borderColor: honeyColors.primary,
    color: honeyColors.textPrimary,
    transform: 'translateY(-1px)',
  },
});


// Add these to your styled.js file

// Base membership status container
export const MembershipStatusContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
});

// Status badge that matches your honey theme
export const StatusBadge = styled(Fab)(({ status, role }) => {
  // Color mapping for different statuses
  const getStatusStyles = () => {
    switch (status) {
      case 'AC': // Active
        return {
          background: 'linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)',
          color: '#FFFFFF',
          boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
            transform: 'translateY(-2px) scale(1.02)',
            boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
          },
        };

      case 'PP': // Pending
        return {
          background: 'linear-gradient(135deg, #FFB74D 0%, #FF9800 100%)',
          color: '#FFFFFF',
          boxShadow: '0 4px 16px rgba(255, 152, 0, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
            transform: 'translateY(-2px) scale(1.02)',
            boxShadow: '0 6px 20px rgba(255, 152, 0, 0.4)',
          },
        };

      case 'RE': // Rejected
        return {
          background: 'linear-gradient(135deg, #EF5350 0%, #F44336 100%)',
          color: '#FFFFFF',
          boxShadow: '0 4px 16px rgba(244, 67, 54, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
            transform: 'translateY(-2px) scale(1.02)',
            boxShadow: '0 6px 20px rgba(244, 67, 54, 0.4)',
          },
        };

      case 'IN': // Inactive
        return {
          background: 'linear-gradient(135deg, #90A4AE 0%, #607D8B 100%)',
          color: '#FFFFFF',
          boxShadow: '0 4px 16px rgba(96, 125, 139, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #607D8B 0%, #455A64 100%)',
            transform: 'translateY(-2px) scale(1.02)',
            boxShadow: '0 6px 20px rgba(96, 125, 139, 0.4)',
          },
        };

      case 'BA': // Banned
        return {
          background: 'linear-gradient(135deg, #424242 0%, #212121 100%)',
          color: '#FFFFFF',
          boxShadow: '0 4px 16px rgba(66, 66, 66, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #212121 0%, #000000 100%)',
            transform: 'translateY(-2px) scale(1.02)',
            boxShadow: '0 6px 20px rgba(66, 66, 66, 0.4)',
          },
        };

      default:
        return {
          background: honeyColors.gradientPrimary,
          color: honeyColors.textPrimary,
          boxShadow: honeyColors.shadowFab,
        };
    }
  };

  const statusStyles = getStatusStyles();

  return {
    minWidth: '80px',
    height: '32px',
    borderRadius: '16px',
    ...statusStyles,
    border: `2px solid rgba(255, 255, 255, 0.2)`,
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',

    '&:active': {
      transform: 'translateY(-1px) scale(0.98)',
    },

    '& .MuiFab-label': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '6px',
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'none',
      letterSpacing: '0.3px',
      lineHeight: 1.2,
    },

    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
    },
  };
});

// Role chip for additional context
export const RoleChip = styled(Chip)(({ role }) => {
  const getRoleStyles = () => {
    switch (role) {
      case 'FO': // Founder
        return {
          backgroundColor: '#9C27B0',
          color: '#FFFFFF',
          '& .MuiChip-icon': { color: '#FFFFFF' },
        };
      case 'AD': // Admin
        return {
          backgroundColor: '#2196F3',
          color: '#FFFFFF',
          '& .MuiChip-icon': { color: '#FFFFFF' },
        };
      case 'MA': // Manager
        return {
          backgroundColor: '#00BCD4',
          color: '#FFFFFF',
          '& .MuiChip-icon': { color: '#FFFFFF' },
        };
      case 'PA': // Participant
        return {
          backgroundColor: '#4CAF50',
          color: '#FFFFFF',
          '& .MuiChip-icon': { color: '#FFFFFF' },
        };
      case 'GU': // Guest
        return {
          backgroundColor: '#607D8B',
          color: '#FFFFFF',
          '& .MuiChip-icon': { color: '#FFFFFF' },
        };
      default:
        return {
          backgroundColor: honeyColors.primary,
          color: honeyColors.textPrimary,
        };
    }
  };

  return {
    borderRadius: '16px',
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '28px',
    ...getRoleStyles(),
  };
});

// Status details tooltip content
export const StatusTooltipContent = styled(Box)({
  padding: '12px',
  maxWidth: '280px',

  '& .status-header': {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    fontWeight: 700,
    fontSize: '0.9rem',
  },

  '& .status-details': {
    fontSize: '0.8rem',
    color: 'text.secondary',
    lineHeight: 1.4,
  },

  '& .status-date': {
    fontSize: '0.75rem',
    color: 'text.disabled',
    marginTop: '4px',
    fontStyle: 'italic',
  },
});